/**
 * Super Admin Notification Settings Screen
 * Allows super admins to customize system-wide notification appearance
 */

import { useState, useEffect, useCallback } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Switch,
  Modal,
  Dimensions,
  TextInput,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import LottieView from "lottie-react-native";
import { useThemeColors } from "@/providers/ThemeProvider";
import { supabase } from "@/lib/supabase/client";
import {
  NOTIFICATION_THEMES,
  getLottieAnimationsList,
  getLottieAnimation,
  DEFAULT_THEME_BY_TYPE,
  DEFAULT_LOTTIE_BY_TYPE,
  NotificationTheme,
} from "@/modules/notifications";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

/**
 * Notification type config
 */
interface NotificationTypeConfig {
  id: string;
  label: string;
  description: string;
  icon: string;
  themeId: string;
  lottieAnimation: string;
  isEnabled: boolean;
  sendPush: boolean;
  /** Editable notification title (from notification_templates) */
  templateTitle: string;
  /** Editable notification body (from notification_templates) */
  templateBody: string;
  /** Placeholder hint shown below the text fields */
  templateHint?: string;
}

const SUPER_ADMIN_NOTIFICATION_TYPES: NotificationTypeConfig[] = [
  {
    id: "role_assignment",
    label: "Rol Toewijzing",
    description: "Wanneer iemand een nieuwe rol krijgt",
    icon: "shield-checkmark-outline",
    themeId: DEFAULT_THEME_BY_TYPE.role_assignment || "blue",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.role_assignment || "Star_Struck_Emoji",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🛡️ Nieuwe rol toegewezen",
    templateBody: "Je hebt een nieuwe rol gekregen in de app.",
  },
  {
    id: "new_weekend",
    label: "Nieuw Familie Weekend",
    description: "Wanneer een nieuw weekend wordt aangemaakt",
    icon: "calendar-outline",
    themeId: DEFAULT_THEME_BY_TYPE.new_weekend || "green",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.new_weekend || "Partying_Emoji",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🏕️ Nieuw Familie Weekend gepland!",
    templateBody: "Er is een nieuw Familie Weekend aangemaakt. Bekijk de details in de app!",
  },
  {
    id: "new_feature",
    label: "Nieuwe Feature",
    description: "Aankondiging van nieuwe app functies",
    icon: "rocket-outline",
    themeId: DEFAULT_THEME_BY_TYPE.new_feature || "purple-orange",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.new_feature || "Rocket_Emoji",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🚀 Nieuwe functie beschikbaar!",
    templateBody: "Er is een nieuwe functie toegevoegd aan de app. Ontdek het nu!",
  },
  {
    id: "system_announcement",
    label: "Systeem Aankondiging",
    description: "Algemene mededelingen voor alle gebruikers",
    icon: "megaphone-outline",
    themeId: DEFAULT_THEME_BY_TYPE.system_announcement || "cream-orange",
    lottieAnimation: DEFAULT_LOTTIE_BY_TYPE.system_announcement || "Waving_Emoji",
    isEnabled: true,
    sendPush: true,
    templateTitle: "📢 Mededeling",
    templateBody: "Er is een nieuwe mededeling voor de Staatsen familie.",
  },
  {
    id: "birthday_reminder",
    label: "Verjaardag Herinnering",
    description: "Dag-voor herinnering aan iedereen (Morgen is {name} jarig!)",
    icon: "gift-outline",
    themeId: DEFAULT_THEME_BY_TYPE.birthday || "cream-orange",
    lottieAnimation: "cake",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🎂 Morgen is {name} jarig!",
    templateBody: "Vergeet niet {name} morgen te feliciteren met hun verjaardag!",
    templateHint: "Gebruik {name} voor de naam van de jarige",
  },
  {
    id: "birthday_personal",
    label: "Persoonlijk Verjaardagsbericht",
    description: "Persoonlijk bericht voor de jarige (op de dag zelf)",
    icon: "balloon-outline",
    themeId: DEFAULT_THEME_BY_TYPE.birthday || "purple-orange",
    lottieAnimation: "confetti",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🎉 Gefeliciteerd {name}!",
    templateBody: "Van harte gefeliciteerd met je verjaardag! De hele Staatsen familie wenst je een fantastische dag! 🎂🎈",
    templateHint: "Gebruik {name} voor de naam van de jarige",
  },
  {
    id: "birthday",
    label: "Verjaardag Melding",
    description: "Melding aan iedereen op de dag zelf (Vandaag is {name} jarig!)",
    icon: "gift-outline",
    themeId: DEFAULT_THEME_BY_TYPE.birthday || "cream-orange",
    lottieAnimation: "cake",
    isEnabled: true,
    sendPush: true,
    templateTitle: "🎂 Vandaag jarig: {name}!",
    templateBody: "Vergeet niet {name} te feliciteren met hun verjaardag!",
    templateHint: "Gebruik {name} voor de naam van de jarige",
  },
];

/**
 * Super Admin Notification Settings Screen Component
 */
export default function SuperAdminNotificationSettingsScreen() {
  const colors = useThemeColors();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState<NotificationTypeConfig[]>(SUPER_ADMIN_NOTIFICATION_TYPES);
  const [hasChanges, setHasChanges] = useState(false);

  // Modal state
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [showLottieModal, setShowLottieModal] = useState(false);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);

  /**
   * Fetch notification settings and templates from database
   */
  const fetchSettings = useCallback(async () => {
    try {
      // Fetch display settings (theme, animation, enabled, push)
      const { data: settingsData, error: settingsError } = await supabase
        .from("notification_type_settings")
        .select("*")
        .is("weekend_id", null);

      if (settingsError) {
        console.error("Error fetching notification settings:", settingsError);
      }

      // Fetch notification templates (title, body)
      const { data: templatesData, error: templatesError } = await supabase
        .from("notification_templates")
        .select("notification_type, title, body")
        .is("weekend_id", null);

      if (templatesError) {
        console.error("Error fetching notification templates:", templatesError);
      }

      // Merge both data sources with defaults
      const updatedTypes = SUPER_ADMIN_NOTIFICATION_TYPES.map(defaultType => {
        const dbSetting = settingsData?.find(s => s.notification_type === defaultType.id);
        const dbTemplate = templatesData?.find(t => t.notification_type === defaultType.id);

        return {
          ...defaultType,
          themeId: dbSetting?.theme_id || defaultType.themeId,
          lottieAnimation: dbSetting?.lottie_animation || defaultType.lottieAnimation,
          isEnabled: dbSetting ? dbSetting.is_enabled : defaultType.isEnabled,
          sendPush: dbSetting ? dbSetting.send_push : defaultType.sendPush,
          templateTitle: dbTemplate?.title || defaultType.templateTitle,
          templateBody: dbTemplate?.body || defaultType.templateBody,
        };
      });
      setNotificationTypes(updatedTypes);
    } catch (err) {
      console.error("Error in fetchSettings:", err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    fetchSettings();
  }, [fetchSettings]);

  /**
   * Save settings and templates to database
   */
  const handleSave = async () => {
    setIsSaving(true);
    try {
      for (const type of notificationTypes) {
        // --- Save display settings (notification_type_settings) ---
        const { data: existingSetting } = await supabase
          .from("notification_type_settings")
          .select("id")
          .is("weekend_id", null)
          .eq("notification_type", type.id)
          .single();

        if (existingSetting) {
          const { error } = await supabase
            .from("notification_type_settings")
            .update({
              theme_id: type.themeId,
              lottie_animation: type.lottieAnimation,
              is_enabled: type.isEnabled,
              send_push: type.sendPush,
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingSetting.id);

          if (error) {
            console.error("Error updating notification setting:", error);
            throw error;
          }
        } else {
          const { error } = await supabase
            .from("notification_type_settings")
            .insert({
              weekend_id: null,
              notification_type: type.id,
              theme_id: type.themeId,
              lottie_animation: type.lottieAnimation,
              is_enabled: type.isEnabled,
              send_push: type.sendPush,
            });

          if (error) {
            console.error("Error inserting notification setting:", error);
            throw error;
          }
        }

        // --- Save template text (notification_templates) ---
        if (type.templateTitle || type.templateBody) {
          const { data: existingTemplate } = await supabase
            .from("notification_templates")
            .select("id")
            .is("weekend_id", null)
            .eq("notification_type", type.id)
            .single();

          if (existingTemplate) {
            const { error } = await supabase
              .from("notification_templates")
              .update({
                title: type.templateTitle,
                body: type.templateBody,
                updated_at: new Date().toISOString(),
              })
              .eq("id", existingTemplate.id);

            if (error) {
              console.error("Error updating notification template:", error);
              throw error;
            }
          } else {
            const { error } = await supabase
              .from("notification_templates")
              .insert({
                weekend_id: null,
                notification_type: type.id,
                title: type.templateTitle,
                body: type.templateBody,
              });

            if (error) {
              console.error("Error inserting notification template:", error);
              throw error;
            }
          }
        }
      }

      setHasChanges(false);
    } catch (err) {
      console.error("Error saving settings:", err);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Update a notification type setting
   */
  const updateTypeSetting = (
    typeId: string,
    updates: Partial<NotificationTypeConfig>
  ) => {
    setNotificationTypes(prev =>
      prev.map(t => (t.id === typeId ? { ...t, ...updates } : t))
    );
    setHasChanges(true);
  };

  /**
   * Open theme selector for a type
   */
  const openThemeSelector = (typeId: string) => {
    setEditingTypeId(typeId);
    setShowThemeModal(true);
  };

  /**
   * Open lottie selector for a type
   */
  const openLottieSelector = (typeId: string) => {
    setEditingTypeId(typeId);
    setShowLottieModal(true);
  };

  /**
   * Select a theme
   */
  const selectTheme = (themeId: string) => {
    if (editingTypeId) {
      updateTypeSetting(editingTypeId, { themeId });
    }
    setShowThemeModal(false);
    setEditingTypeId(null);
  };

  /**
   * Select a lottie animation
   */
  const selectLottie = (lottieId: string) => {
    if (editingTypeId) {
      updateTypeSetting(editingTypeId, { lottieAnimation: lottieId });
    }
    setShowLottieModal(false);
    setEditingTypeId(null);
  };

  /**
   * Get theme by ID
   */
  const getTheme = (themeId: string): NotificationTheme => {
    return NOTIFICATION_THEMES.find(t => t.id === themeId) || NOTIFICATION_THEMES[0];
  };

  /**
   * Render a notification type card
   */
  const renderNotificationType = (type: NotificationTypeConfig) => {
    const theme = getTheme(type.themeId);

    return (
      <View
        key={type.id}
        className="rounded-2xl overflow-hidden mb-4"
        style={{
          backgroundColor: colors.surface,
          borderWidth: 1,
          borderColor: colors.border,
        }}
      >
        {/* Header */}
        <View className="flex-row items-center p-4" style={{ borderBottomWidth: 1, borderBottomColor: colors.border }}>
          <View
            className="w-10 h-10 rounded-full items-center justify-center mr-3"
            style={{ backgroundColor: colors.primary + "20" }}
          >
            <Ionicons name={type.icon as any} size={20} color={colors.primary} />
          </View>
          <View className="flex-1">
            <Text className="font-semibold" style={{ color: colors.text }}>
              {type.label}
            </Text>
            <Text className="text-xs" style={{ color: colors.textSecondary }}>
              {type.description}
            </Text>
          </View>
          <Switch
            value={type.isEnabled}
            onValueChange={(value) => updateTypeSetting(type.id, { isEnabled: value })}
            trackColor={{ false: colors.border, true: colors.primary + "60" }}
            thumbColor={type.isEnabled ? colors.primary : colors.textTertiary}
          />
        </View>

        {/* Settings */}
        {type.isEnabled && (
          <View className="p-4">
            {/* Theme Preview */}
            <TouchableOpacity
              onPress={() => openThemeSelector(type.id)}
              className="flex-row items-center mb-4"
              accessibilityLabel="Kies achtergrond thema"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Achtergrond
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {theme.name}
                </Text>
              </View>
              <View className="rounded-xl overflow-hidden" style={{ width: 60, height: 40 }}>
                <LinearGradient
                  colors={[theme.gradient_start, theme.gradient_middle || theme.gradient_end, theme.gradient_end]}
                  style={{ flex: 1 }}
                />
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} style={{ marginLeft: 8 }} />
            </TouchableOpacity>

            {/* Lottie Preview */}
            <TouchableOpacity
              onPress={() => openLottieSelector(type.id)}
              className="flex-row items-center mb-4"
              accessibilityLabel="Kies animatie"
            >
              <View className="flex-1">
                <Text className="text-sm font-medium mb-1" style={{ color: colors.text }}>
                  Animatie
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  {getLottieAnimationsList().find(l => l.id === type.lottieAnimation)?.name || type.lottieAnimation}
                </Text>
              </View>
              <View className="rounded-xl overflow-hidden" style={{ width: 48, height: 48, backgroundColor: colors.surfaceSecondary }}>
                <LottieView
                  source={getLottieAnimation(type.lottieAnimation)}
                  style={{ width: 48, height: 48 }}
                  autoPlay
                  loop
                />
              </View>
              <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} style={{ marginLeft: 8 }} />
            </TouchableOpacity>

            {/* Template text editing */}
            <View className="mb-4 rounded-xl p-3" style={{ backgroundColor: colors.surfaceSecondary }}>
              <Text className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                Bericht Tekst
              </Text>

              {/* Title */}
              <Text className="text-xs mb-1" style={{ color: colors.textSecondary }}>
                Titel
              </Text>
              <TextInput
                value={type.templateTitle}
                onChangeText={(text) => updateTypeSetting(type.id, { templateTitle: text })}
                className="rounded-lg px-3 py-2 mb-3 text-sm"
                style={{
                  backgroundColor: colors.background,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                }}
                placeholder="Titel van de notificatie"
                placeholderTextColor={colors.textTertiary}
                accessibilityLabel={`Titel voor ${type.label}`}
              />

              {/* Body */}
              <Text className="text-xs mb-1" style={{ color: colors.textSecondary }}>
                Bericht
              </Text>
              <TextInput
                value={type.templateBody}
                onChangeText={(text) => updateTypeSetting(type.id, { templateBody: text })}
                className="rounded-lg px-3 py-2 text-sm"
                style={{
                  backgroundColor: colors.background,
                  color: colors.text,
                  borderWidth: 1,
                  borderColor: colors.border,
                  minHeight: 60,
                }}
                placeholder="Bericht van de notificatie"
                placeholderTextColor={colors.textTertiary}
                multiline
                textAlignVertical="top"
                accessibilityLabel={`Bericht voor ${type.label}`}
              />

              {/* Placeholder hint */}
              {type.templateHint && (
                <Text className="text-xs mt-2" style={{ color: colors.primary }}>
                  💡 {type.templateHint}
                </Text>
              )}
            </View>

            {/* Push notification toggle */}
            <View className="flex-row items-center justify-between">
              <View>
                <Text className="text-sm font-medium" style={{ color: colors.text }}>
                  Push Notificatie
                </Text>
                <Text className="text-xs" style={{ color: colors.textSecondary }}>
                  Ook sturen als app niet open is
                </Text>
              </View>
              <Switch
                value={type.sendPush}
                onValueChange={(value) => updateTypeSetting(type.id, { sendPush: value })}
                trackColor={{ false: colors.border, true: colors.primary + "60" }}
                thumbColor={type.sendPush ? colors.primary : colors.textTertiary}
              />
            </View>
          </View>
        )}
      </View>
    );
  };

  /**
   * Render theme selection modal
   */
  const renderThemeModal = () => (
    <Modal
      visible={showThemeModal}
      animationType="slide"
      transparent
      onRequestClose={() => setShowThemeModal(false)}
    >
      <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
        <View
          className="rounded-t-3xl p-6"
          style={{ backgroundColor: colors.surface, maxHeight: "80%" }}
        >
          <View className="flex-row justify-between items-center mb-4">
            <Text className="text-xl font-bold" style={{ color: colors.text }}>
              Kies Achtergrond
            </Text>
            <TouchableOpacity onPress={() => setShowThemeModal(false)}>
              <Ionicons name="close" size={24} color={colors.text} />
            </TouchableOpacity>
          </View>

          <ScrollView showsVerticalScrollIndicator={false}>
            <View className="flex-row flex-wrap justify-between">
              {NOTIFICATION_THEMES.map((theme) => (
                <TouchableOpacity
                  key={theme.id}
                  onPress={() => selectTheme(theme.id)}
                  className="mb-4 rounded-2xl overflow-hidden"
                  style={{
                    width: (SCREEN_WIDTH - 60) / 2,
                    height: 120,
                    borderWidth: editingTypeId && notificationTypes.find(t => t.id === editingTypeId)?.themeId === theme.id ? 3 : 0,
                    borderColor: colors.primary,
                  }}
                >
                  <LinearGradient
                    colors={[theme.gradient_start, theme.gradient_middle || theme.gradient_end, theme.gradient_end]}
                    style={{ flex: 1, justifyContent: "flex-end", padding: 12 }}
                  >
                    <Text className="font-semibold" style={{ color: "white" }}>
                      {theme.name}
                    </Text>
                  </LinearGradient>
                </TouchableOpacity>
              ))}
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  /**
   * Render lottie selection modal
   */
  const renderLottieModal = () => {
    const animations = getLottieAnimationsList();

    return (
      <Modal
        visible={showLottieModal}
        animationType="slide"
        transparent
        onRequestClose={() => setShowLottieModal(false)}
      >
        <View className="flex-1 justify-end" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <View
            className="rounded-t-3xl p-6"
            style={{ backgroundColor: colors.surface, maxHeight: "80%" }}
          >
            <View className="flex-row justify-between items-center mb-4">
              <Text className="text-xl font-bold" style={{ color: colors.text }}>
                Kies Animatie
              </Text>
              <TouchableOpacity onPress={() => setShowLottieModal(false)}>
                <Ionicons name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <View className="flex-row flex-wrap justify-between">
                {animations.map((anim) => (
                  <TouchableOpacity
                    key={anim.id}
                    onPress={() => selectLottie(anim.id)}
                    className="mb-4 rounded-2xl items-center justify-center p-2"
                    style={{
                      width: (SCREEN_WIDTH - 72) / 3,
                      height: 100,
                      backgroundColor: colors.surfaceSecondary,
                      borderWidth: editingTypeId && notificationTypes.find(t => t.id === editingTypeId)?.lottieAnimation === anim.id ? 3 : 0,
                      borderColor: colors.primary,
                    }}
                  >
                    <LottieView
                      source={anim.file}
                      style={{ width: 56, height: 56 }}
                      autoPlay
                      loop
                    />
                    <Text
                      className="text-xs text-center mt-1"
                      style={{ color: colors.textSecondary }}
                      numberOfLines={1}
                    >
                      {anim.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  if (isLoading) {
    return (
      <SafeAreaView
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
        edges={["bottom"]}
      >
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["bottom"]}
    >
      <ScrollView
        className="flex-1 px-4"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header info */}
        <View
          className="rounded-2xl p-4 mb-6 flex-row items-start"
          style={{ backgroundColor: "#EF444410" }}
        >
          <Ionicons name="shield-checkmark" size={24} color="#EF4444" />
          <Text className="flex-1 ml-3 text-sm" style={{ color: colors.text }}>
            Deze instellingen gelden voor alle gebruikers in de app. Wijzigingen worden direct toegepast.
          </Text>
        </View>

        {/* Notification Types */}
        {notificationTypes.map(renderNotificationType)}

        {/* Save button */}
        {hasChanges && (
          <TouchableOpacity
            onPress={handleSave}
            disabled={isSaving}
            className="rounded-xl p-4 mb-8 flex-row items-center justify-center"
            style={{ backgroundColor: "#EF4444" }}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <>
                <Ionicons name="save-outline" size={20} color="white" />
                <Text className="ml-2 font-semibold" style={{ color: "white" }}>
                  Instellingen Opslaan
                </Text>
              </>
            )}
          </TouchableOpacity>
        )}

        <View className="h-8" />
      </ScrollView>

      {/* Modals */}
      {renderThemeModal()}
      {renderLottieModal()}
    </SafeAreaView>
  );
}

